import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { supabase } from "../supabaseClient";
import PublicSiteLayout from "./PublicSiteLayout";
import "./PublicInformationPages.css";

export default function Support() {
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const successRef = useRef(null);
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (pending.current || !feedback.trim()) return;
    pending.current = true;
    setSubmitting(true);
    setError("");
    try {
      const { error: requestError } = await supabase.from("feedback").insert({ type: "website", message: feedback.trim() });
      if (requestError) throw requestError;
      setSubmitted(true);
      setFeedback("");
      requestAnimationFrame(() => successRef.current?.focus());
    } catch {
      setError("Your feedback could not be saved. Your text is still here. Try again, or email support@snowsproam.com.");
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };
  return <PublicSiteLayout className="pi-page">
    <section className="ps-wrap pi-hero">
      <span className="ps-eyebrow">Support</span>
      <h1>Help with Snows ProAm.</h1>
      <p className="ps-lead">Find an answer, report a problem or tell us what could work better.</p>
    </section>
    <div className="ps-wrap pi-support-layout">
      <section aria-labelledby="support-start-title">
        <h2 id="support-start-title">Account and booking help.</h2>
        <p className="ps-lead">For account, booking or payment help, email our support team. Include the email on your account and what happened.</p>
        <p className="pi-contact-line"><a href="mailto:support@snowsproam.com">support@snowsproam.com</a></p>
        <nav className="pi-support-links" aria-label="Help topics">
          <Link to="/faq"><span>Frequently asked questions<small>Getting started, training and finding your sport.</small></span><ArrowRight size={19}/></Link>
          <Link to="/privacypolicy#policy-12"><span>Account deletion and privacy<small>Understand your data and how to request deletion.</small></span><ArrowRight size={19}/></Link>
          <Link to="/coaching"><span>Workout groups and coaching<small>See how athletes and coaches train together.</small></span><ArrowRight size={19}/></Link>
        </nav>
      </section>
      <section className="pi-feedback" aria-labelledby="support-feedback-title">
        {submitted ? <div className="pi-feedback-success" tabIndex={-1} ref={successRef} role="status">
          <CheckCircle2 size={32}/><h2 id="support-feedback-title">Feedback received.</h2>
          <p>Thank you for helping improve Snows ProAm. If you need a reply about your account, email our support team.</p>
          <button type="button" className="ps-button ps-button-secondary" onClick={() => setSubmitted(false)}>Send more feedback</button>
        </div> : <>
          <h2 id="support-feedback-title">Share feedback.</h2>
          <p>Report a problem or suggest an improvement. Please leave out passwords and payment details.</p>
          <form onSubmit={handleSubmit}>
            <label htmlFor="support-feedback">Your feedback</label>
            <textarea id="support-feedback" name="feedback" required maxLength={5000} value={feedback} onChange={(event) => setFeedback(event.target.value)} aria-describedby={error ? "support-feedback-error" : "support-feedback-help"} aria-invalid={!!error} disabled={submitting}/>
            <small id="support-feedback-help">For a personal reply, email support@snowsproam.com.</small>
            {error && <p id="support-feedback-error" className="pi-error" role="alert">{error}</p>}
            <button type="submit" className="ps-button" disabled={submitting} aria-busy={submitting}>{submitting ? "Sending…" : "Send feedback"}<ArrowRight size={17}/></button>
          </form>
        </>}
      </section>
    </div>
  </PublicSiteLayout>;
}
